import { find, noop, unionBy, sortBy } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Dropdown, DropdownContainer } from '@zocdoc/components/src/dropdown';
import { filterToValidAttributes } from '@zocdoc/dom-property';

export const LOCALIZED_STRINGS = defineMessages({
    Label: {
        id: 'Components.RefineSearch.VisitReasonsDropdown.Label',
        defaultMessage: 'visit reason:',
    },
    ChooseAVisitReason: {
        id: 'Components.RefineSearch.VisitReasonsDropdown.ChooseAVisitReason',
        defaultMessage: 'choose a visit reason',
    },
});

class BaseVisitReasonsDropdown extends Component {
    static propTypes = {
        onChangeCallback: PropTypes.func.isRequired,
        procedureId: PropTypes.string.isRequired,
        trackRefineSearchClickProcedureDropdown: PropTypes.func,
        trackRefineSearchSelectProcedure: PropTypes.func,
        visitReasons: PropTypes.array.isRequired,
    };

    static defaultProps = {
        trackRefineSearchSelectProcedure: noop,
    };

    selectedOption;

    // block updates to the component while proceduresBySpecialty
    // are loading to avoid visual flash
    shouldComponentUpdate(nextProps) {
        return nextProps.visitReasons.length;
    }

    handleChange = update => {
        const procedure = { procedureId: update };
        this.selectedOption = find(this.props.visitReasons, { id: update });

        this.props.trackRefineSearchSelectProcedure(procedure);
        this.props.onChangeCallback(procedure);
    }

    render() {
        const {
            intl,
            procedureId,
            visitReasons,
            ...props
        } = this.props;

        let options = visitReasons;

        if (this.selectedOption) {
            // If visitReasons changes, there is a chance the selected procedure is no longer inside the options.
            // This code ensures that the selected procedure is always found inside the dropdown.
            options = sortBy(
                unionBy(visitReasons, [this.selectedOption], 'id'),
                ['name'],
            );
        }

        return (
            <Container
                {...filterToValidAttributes(props)}
                data-test="search-visit-reasons-dropdown-container"
            >
                <DropdownLabel>
                    <FormattedMessage {...LOCALIZED_STRINGS.Label} />
                </DropdownLabel>

                <StyledDropdownContainer
                    data-test="search-visit-reasons-dropdown-container-inner"
                    onChange={this.handleChange}
                    selected={procedureId}
                >
                    <StyledDropdown
                        data-test="search-visit-reasons-dropdown"
                        inline
                        onClick={this.props.trackRefineSearchClickProcedureDropdown}
                        options={options}
                        placeholder={intl.formatMessage(LOCALIZED_STRINGS.ChooseAVisitReason)}
                    />
                </StyledDropdownContainer>
            </Container>
        );
    }
}

const Container = styled.div`
    align-items: center;
    display: flex;
    overflow: hidden;
`;

export const DropdownLabel = styled.div`
    flex-shrink: 0;
    margin-right: 10px;
    white-space: nowrap;
`;

const StyledDropdownContainer = styled(DropdownContainer)`
    color: ${p => p.theme.search.header.filters.textColor};
    flex-basis: auto;
    width: auto;
`;

const StyledDropdown = styled(Dropdown)`
    background: transparent;
    border: 0;
    line-height: 1;
    min-width: 50px;
`;

export default injectIntl(BaseVisitReasonsDropdown);
